import US from "country-flag-icons/react/1x1/US";
import EU from "country-flag-icons/react/1x1/EU";
import PY from "country-flag-icons/react/1x1/PY";
import BR from "country-flag-icons/react/1x1/BR";
import CA from "country-flag-icons/react/1x1/CA";
import CH from "country-flag-icons/react/1x1/CH";
import JP from "country-flag-icons/react/1x1/JP";
import AU from "country-flag-icons/react/1x1/AU";
import MX from "country-flag-icons/react/1x1/MX";
import SE from "country-flag-icons/react/1x1/SE";
import {
  buildBaasCdnIconUrl,
  resolveBaasIconUrl,
  resolveCdnIconsBaseUrl,
} from "../../../Pages/Private/BaasConsole/Extrato/baasCdnIcons";

export const DEFAULT_CURRENCY_ICON_SIZE = 18;

const ASSETS = {
  usd: { type: "flag", Flag: US, label: "USD" },
  eur: { type: "flag", Flag: EU, label: "EUR" },
  pyg: { type: "flag", Flag: PY, label: "PYG" },
  brl: { type: "flag", Flag: BR, label: "BRL" },
  brl1: { type: "flag", Flag: BR, label: "BRL1" },
  cad: { type: "flag", Flag: CA, label: "CAD" },
  chf: { type: "flag", Flag: CH, label: "CHF" },
  jpy: { type: "flag", Flag: JP, label: "JPY" },
  aud: { type: "flag", Flag: AU, label: "AUD" },
  mxn: { type: "flag", Flag: MX, label: "MXN" },
  sek: { type: "flag", Flag: SE, label: "SEK" },
  usdc: { type: "stable", label: "USDC" },
  usdt: { type: "stable", label: "USDT" },
  eurc: { type: "stable", label: "EURC" },
};

/** Stables always use Front Door CDN (`…/assets/icons/{symbol}.svg`), never bundled SVGs. */
const PREFER_REMOTE_ICON = new Set(["brl1", "usdc", "usdt", "eurc"]);

/** CDN / remote icon stem aliases (EURC reuses USDC art until a dedicated asset exists). */
const ICON_SYMBOL_ALIASES = {
  eurc: "usdc",
};

const buildIconStyle = (size) => ({
  width: size,
  height: size,
  minWidth: size,
  minHeight: size,
});

const resolveAssetKey = (asset) => String(asset ?? "").trim().toLowerCase();

const resolveIconSymbol = (assetKey) =>
  ICON_SYMBOL_ALIASES[assetKey] || assetKey;

const isConfiguredCdnIconUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return false;
  if (/^https:\/\/cdn\.valut\.services\/assets\/icons\//i.test(raw)) return true;
  const cdnBase = resolveCdnIconsBaseUrl();
  return Boolean(cdnBase && (raw === cdnBase || raw.startsWith(`${cdnBase}/`)));
};

const isValutBalanceIconUrl = (url) =>
  /^https:\/\/static\.valut\.app\/balance-icons\//i.test(String(url || "").trim());

const isValutStatementStableIconUrl = (url) =>
  /\/(?:compra|venda)-stable\.svg(?:[?#]|$)/i.test(String(url || "").trim());

/** Stables: Front Door CDN from symbol; keep Valut category icons when API sends them. */
const resolveRemoteIconSrc = (assetKey, iconUrl) => {
  const iconSymbol = resolveIconSymbol(assetKey);
  const rawIcon = String(iconUrl || "").trim();
  // Prefer Valut category icons over brand USDC/USDT coin art.
  if (isValutBalanceIconUrl(rawIcon) || isValutStatementStableIconUrl(rawIcon)) {
    return rawIcon;
  }

  const resolved = resolveBaasIconUrl(iconUrl, { symbol: iconSymbol });
  if (resolved && (isValutBalanceIconUrl(resolved) || isValutStatementStableIconUrl(resolved))) {
    return resolved;
  }
  if (isConfiguredCdnIconUrl(resolved)) {
    // Remap EURC (etc.) CDN URLs to the aliased stem when API still points at eurc.svg.
    if (iconSymbol !== assetKey && /\/eurc\.svg(?:[?#]|$)/i.test(resolved)) {
      return buildBaasCdnIconUrl(iconSymbol) || resolved;
    }
    return resolved;
  }
  return buildBaasCdnIconUrl(iconSymbol) || null;
};

export const CurrencyIconSlot = ({ children }) => (
  <div className="flex w-10 shrink-0 items-center">{children}</div>
);

export const CurrencyIconPair = ({ from, to, size = DEFAULT_CURRENCY_ICON_SIZE }) => (
  <div className="flex items-center -space-x-1">
    <CurrencyIcon asset={from} size={size} />
    <CurrencyIcon asset={to} size={size} className="ring-2 ring-white" />
  </div>
);

export const CurrencyIcon = ({
  asset,
  iconUrl,
  className = "",
  size = DEFAULT_CURRENCY_ICON_SIZE,
  /**
   * Kept for call-site compatibility (cotações). Brand USDC/USDT/EURC icons always
   * render in their CDN colors — the old gold CSS filter flattened USDC into a
   * solid mustard circle on light balance cards.
   */
  preserveColor: _preserveColor = false,
}) => {
  const assetKey = resolveAssetKey(asset);
  const config = ASSETS[assetKey];
  const label = config?.label ?? assetKey.toUpperCase();

  if (PREFER_REMOTE_ICON.has(assetKey)) {
    const src = resolveRemoteIconSrc(assetKey, iconUrl);
    if (src) {
      return (
        <img
          src={src}
          alt={label}
          className={`currency-icon shrink-0 rounded-full object-cover ${className}`.trim()}
          style={buildIconStyle(size)}
          width={size}
          height={size}
          decoding="async"
        />
      );
    }
  }

  if (config?.type === "flag") {
    const { Flag } = config;
    return (
      <div
        className={`currency-icon shrink-0 overflow-hidden rounded-full ${className}`}
        style={buildIconStyle(size)}
      >
        <Flag title={label} style={{ ...buildIconStyle(size), display: "block" }} />
      </div>
    );
  }

  if (iconUrl) {
    const src =
      resolveBaasIconUrl(iconUrl, { symbol: resolveIconSymbol(assetKey) }) || iconUrl;
    return (
      <img
        src={src}
        alt={label}
        className={`currency-icon shrink-0 rounded-full object-cover ${className}`.trim()}
        style={buildIconStyle(size)}
        width={size}
        height={size}
        decoding="async"
      />
    );
  }

  return null;
};

export const CURRENCY_ASSETS = Object.keys(ASSETS);
